import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Wallet2, ShieldCheck, ExternalLink, X as XIcon, Loader2, Lock } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import { getDiplomaI18n } from "../../i18n/diploma";
import AirdropNotification from "../diploma/AirdropNotification";

import API from "../../lib/api";
const WEB3_ENABLED = process.env.REACT_APP_WEB3_ENABLED === "true";

/**
 * DiplomaModal — opens when the user just completed all lessons of a level.
 * Mints an on-chain NFT diploma on Base L2 via DropERC1155.claim().
 *
 * Gated behind REACT_APP_WEB3_ENABLED env flag. When the flag is "false"
 * (e.g. in environments without Web3 support), the modal renders a
 * "coming-soon" placeholder and never imports ethers/diplomaChain.
 *
 * Props:
 *   nivel: 1-6
 *   onClose: () => void
 */
export const DiplomaModal = ({ nivel, onClose }) => {
  const { lang } = useLanguage();
  const T = useMemo(() => getDiplomaI18n(lang), [lang]);

  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | connecting | minting | success | error
  const [errKey, setErrKey] = useState(null);
  const [result, setResult] = useState(null);
  const [airdrop, setAirdrop] = useState(null);

  // Load on-chain config (contract, chain, tokenId)
  useEffect(() => {
    fetch(`${API}/diploma/config`).then(r => r.json()).then(setConfig).catch(() => {});
  }, []);

  const tokenId = config?.token_id_by_nivel?.[String(nivel)];
  const previewSrc = `${API}/diploma/image?nivel=${nivel}&lang=${lang}`;

  const handleConnectAndMint = async () => {
    if (!WEB3_ENABLED) return; // safety — should never trigger when disabled
    setErrKey(null);
    // Lazy-load ethers/diplomaChain only when the user actually clicks mint.
    // This keeps webpack from bundling ethers in environments where Web3 is disabled.
    let chain;
    try {
      chain = await import("../../lib/diplomaChain");
    } catch (e) {
      setErrKey("errNoWallet");
      setStatus("error");
      return;
    }
    if (!chain.hasWalletInjected()) {
      setErrKey("errNoWallet");
      setStatus("error");
      return;
    }
    setStatus("connecting");
    try {
      const { address } = await chain.connectWallet();
      setStatus("minting");

      const { txHash, walletAddress: wa } = await chain.claimDiploma({
        contract: config.contract,
        tokenId: BigInt(tokenId),
      });

      // Record server-side
      const token = localStorage.getItem("rebel_jwt");
      const headers = { "Content-Type": "application/json" };
      if (token) headers.Authorization = `Bearer ${token}`;
      const recRes = await fetch(`${API}/diploma/record`, {
        method: "POST",
        credentials: "include",
        headers,
        body: JSON.stringify({ nivel, tx_hash: txHash, wallet_address: wa }),
      });
      const recData = await recRes.json().catch(() => ({}));

      // R₿L airdrop (PRO-only, additive & non-blocking — the diploma is
      // valid regardless of the airdrop outcome).
      try {
        const adRes = await fetch(`${API}/airdrop/diploma`, {
          method: "POST",
          credentials: "include",
          headers,
          body: JSON.stringify({
            wallet_address: wa,
            token_id: Number(tokenId),
            mint_tx_hash: txHash,
          }),
        });
        const adData = await adRes.json().catch(() => null);
        if (adRes.ok) setAirdrop(adData);
        else setAirdrop({ error: adData?.detail?.error || "airdrop_failed" });
      } catch (_e) {
        setAirdrop({ error: "network" });
      }

      setResult({
        txHash,
        walletAddress: wa,
        basescanUrl: recData?.diploma?.basescan_url || `https://basescan.org/tx/${txHash}`,
        openseaUrl: recData?.diploma?.opensea_url || `https://opensea.io/assets/base/${config.contract}/${tokenId}`,
      });
      setStatus("success");
      // address is implicitly bound to `wa`; mark as used to satisfy linter
      void address;
    } catch (e) {
      const k = chain.mapEthersError(e);
      setErrKey({
        NO_WALLET: "errNoWallet",
        REJECTED: "errReject",
        INSUFFICIENT_FUNDS: "errGas",
        CLAIM_NOT_OPEN: "errClaim",
        ALREADY_CLAIMED: "errClaim",
        GENERIC: "errGeneric",
      }[k] || "errGeneric");
      setStatus("error");
    }
  };

  const overlay = (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
      className="fixed inset-0 z-[9500] flex items-center justify-center p-4"
      style={{ background: "rgba(7,8,15,0.92)", backdropFilter: "blur(10px)" }}
      data-testid="diploma-modal"
    >
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-[520px] rounded-2xl border border-amber-400/30 bg-[#0d0e1c] p-6 shadow-[0_0_60px_rgba(245,166,35,0.18)] md:p-7"
        style={{ fontFamily: "'Courier New', monospace" }}
      >
        <button
          onClick={onClose}
          aria-label="close"
          data-testid="diploma-modal-close"
          className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-black/40 text-white/60 transition-all hover:border-white/40 hover:text-white"
        >
          <XIcon className="h-4 w-4" />
        </button>

        {/* Preview */}
        <div className="mb-4 overflow-hidden rounded-lg border border-amber-400/20" style={{ background: "#07080f" }}>
          <img
            src={status === "success" && result?.txHash
              ? `${API}/diploma/image?nivel=${nivel}&hash=${result.txHash}&lang=${lang}`
              : previewSrc}
            alt={`Diploma Nivel ${nivel}`}
            className="block w-full"
            data-testid="diploma-preview-img"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        </div>

        <div className="mb-1 flex items-center justify-between">
          <span className="inline-flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-400/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-amber-300">
            {WEB3_ENABLED ? <ShieldCheck className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
            {!WEB3_ENABLED ? T.comingSoonBadge : (status === "success" ? T.badgeMinted : T.badgePreview)}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-300/70">
            ⛓ BASE L2 · #{nivel}
          </span>
        </div>

        <h2 className="text-xl font-black tracking-tight text-amber-300">
          {WEB3_ENABLED ? T.modalTitle : T.comingSoonTitle}
        </h2>
        <p className="mt-1 text-sm text-white/55">
          {WEB3_ENABLED ? T.modalSub : T.comingSoonSub}
        </p>

        {/* COMING SOON state — Web3 disabled by env flag */}
        {!WEB3_ENABLED && (
          <div className="mt-5">
            <button
              onClick={onClose}
              data-testid="diploma-continue-btn"
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-amber-400/40 bg-amber-400/10 px-5 py-3 text-xs font-black uppercase tracking-[0.18em] text-amber-200 transition-all hover:bg-amber-400/20"
            >
              {T.continue}
            </button>
          </div>
        )}

        {/* IDLE — primary CTA */}
        {WEB3_ENABLED && status === "idle" && (
          <div className="mt-5 flex flex-col gap-3">
            <button
              onClick={handleConnectAndMint}
              data-testid="diploma-mint-btn"
              className="group relative inline-flex w-full items-center justify-center gap-2 overflow-hidden rounded-xl bg-gradient-to-r from-amber-400 to-amber-500 px-5 py-3 text-xs font-black uppercase tracking-[0.18em] text-black shadow-[0_0_28px_rgba(245,166,35,0.35)] transition-all hover:-translate-y-0.5 hover:shadow-[0_0_44px_rgba(245,166,35,0.6)]"
            >
              <Wallet2 className="h-4 w-4" />
              {T.btnMint}
            </button>
            <div className="text-center font-mono text-[10px] uppercase tracking-[0.18em] text-white/35">
              {T.paidGas}
            </div>
            <button
              onClick={onClose}
              data-testid="diploma-skip-btn"
              className="rounded-lg border border-white/10 px-3 py-2 text-xs font-medium text-white/45 transition-all hover:border-white/30 hover:text-white/70"
            >
              {T.skip}
            </button>
          </div>
        )}

        {/* CONNECTING / MINTING */}
        {WEB3_ENABLED && (status === "connecting" || status === "minting") && (
          <div className="mt-6 flex flex-col items-center gap-2 py-3">
            <Loader2 className="h-7 w-7 animate-spin text-amber-300" />
            <p className="text-sm text-amber-200">
              {status === "connecting" ? T.connecting : T.minting}
            </p>
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/30">
              {status === "minting" ? T.waitConfirm : ""}
            </p>
          </div>
        )}

        {/* SUCCESS */}
        {WEB3_ENABLED && status === "success" && result && (
          <div className="mt-5" data-testid="diploma-success">
            <div className="mb-3 rounded-lg border border-emerald-400/30 bg-emerald-400/10 p-3 text-center">
              <p className="text-sm font-bold tracking-[0.05em] text-emerald-300">{T.okTitle}</p>
              <p className="mt-1 text-xs text-emerald-200/75">{T.okSub}</p>
            </div>
            {/* R₿L airdrop result (PRO reward) */}
            <AirdropNotification airdrop={airdrop} tokenId={Number(tokenId)} />
            <div className="grid grid-cols-1 gap-2">
              <a href={result.basescanUrl} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center justify-center gap-2 rounded-lg border border-cyan-400/30 bg-cyan-400/10 px-3 py-2 text-xs font-bold text-cyan-300 transition-all hover:bg-cyan-400/20"
                 data-testid="diploma-basescan-link">
                <ExternalLink className="h-3.5 w-3.5" /> {T.basescan}
              </a>
              <a href={result.openseaUrl} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center justify-center gap-2 rounded-lg border border-fuchsia-400/30 bg-fuchsia-400/10 px-3 py-2 text-xs font-bold text-fuchsia-300 transition-all hover:bg-fuchsia-400/20"
                 data-testid="diploma-opensea-link">
                <ExternalLink className="h-3.5 w-3.5" /> {T.opensea}
              </a>
              <a href={`/verify?hash=${result.txHash}`}
                 className="inline-flex items-center justify-center gap-2 rounded-lg border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-xs font-bold text-amber-300 transition-all hover:bg-amber-400/20"
                 data-testid="diploma-verify-link">
                <ShieldCheck className="h-3.5 w-3.5" /> {T.verifyLink}
              </a>
              <button
                onClick={onClose}
                className="mt-1 rounded-lg border border-white/10 px-3 py-2 text-xs font-medium text-white/55 transition-all hover:border-white/30 hover:text-white/80"
              >
                {T.close}
              </button>
            </div>
            <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.15em] text-white/30">
              {T.walletShort} {result.walletAddress?.slice(0, 8)}…{result.walletAddress?.slice(-6)}
            </p>
          </div>
        )}

        {/* ERROR */}
        {WEB3_ENABLED && status === "error" && (
          <div className="mt-5" data-testid="diploma-error">
            <div className="mb-3 rounded-lg border border-rose-400/30 bg-rose-400/10 p-3 text-center text-sm text-rose-200">
              {T[errKey] || T.errGeneric}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => { setStatus("idle"); setErrKey(null); }}
                className="rounded-lg border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-xs font-bold text-amber-300 transition-all hover:bg-amber-400/20"
              >
                {T.retry}
              </button>
              <button
                onClick={onClose}
                className="rounded-lg border border-white/10 px-3 py-2 text-xs font-medium text-white/55 transition-all hover:border-white/30 hover:text-white/80"
              >
                {T.close}
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );

  if (typeof document === "undefined") return null;
  return createPortal(
    <AnimatePresence>{overlay}</AnimatePresence>,
    document.body
  );
};

export default DiplomaModal;
